<template>
    <div class="relative inline-block w-full">
        <!-- Input Field -->
        <div class="relative">
            <input
                ref="input"
                type="text"
                :value="formattedDateTime"
                :placeholder="placeholder"
                :disabled="disabled"
                :class="[
                    'w-full px-3 py-1.5 pr-10 border rounded-md shadow-sm bg-theme-canvas text-theme-base transition-colors focus:outline-none focus:ring-1',
                    'focus:outline-none focus:ring-2 border-theme-border focus:ring-theme-primary',
                    disabled ? 'cursor-not-allowed' : ''
                ]"
                readonly
                @click="toggleCalendar"
                @keydown.enter.prevent="toggleCalendar"
                @keydown.space.prevent="toggleCalendar"
                @keydown.esc="closeCalendar"
            />
            <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            >
                <ClockIcon class="w-5 h-5 text-theme-base" />
            </div>
        </div>

        <div
            v-if="showCalendar || alwaysOpen"
            class="absolute z-50 mt-1 bg-theme-canvas text-theme-base border border-theme-border rounded-lg shadow-lg max-h-96 overflow-y-auto"
            :class="dropdownPosition"
        >
            <!-- Time Picker -->
            <div
                class="px-3 py-1 border-b border-theme-border flex items-center"
            >
                <span class="text-xs font-medium text-theme-muted"> Hora </span>
                <div class="flex items-center justify-center gap-1.5 w-full">
                    <select
                        v-model="selectedHour"
                        class="px-2 py-1 text-sm border border-theme-border rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary bg-theme-canvas text-theme-base"
                        @change="updateDateTime"
                    >
                        <option v-for="hour in hours" :key="hour" :value="hour">
                            {{ hour.toString().padStart(2, "0") }}
                        </option>
                    </select>
                    <span class="text-base font-semibold text-theme-base"
                        >:</span
                    >
                    <select
                        v-model="selectedMinute"
                        class="px-2 py-1 text-sm border border-theme-border rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary bg-theme-canvas text-theme-base"
                        @change="updateDateTime"
                    >
                        <option
                            v-for="minute in minutes"
                            :key="minute"
                            :value="minute"
                        >
                            {{ minute.toString().padStart(2, "0") }}
                        </option>
                    </select>
                </div>
            </div>
            <!-- Header -->
            <div
                class="flex items-center justify-between px-3 py-1 border-b border-theme-border sticky top-0 bg-theme-canvas"
            >
                <button
                    type="button"
                    class="p-1 rounded-md hover:bg-theme-hover transition"
                    @click="previousMonth"
                >
                    <ChevronLeftIcon class="w-5 h-5 text-theme-base" />
                </button>
                <div class="font-semibold text-theme-base">
                    {{ monthNames[currentMonth] }} {{ currentYear }}
                </div>
                <button
                    type="button"
                    class="p-1 rounded-md hover:bg-theme-hover transition"
                    @click="nextMonth"
                >
                    <ChevronRightIcon class="w-5 h-5 text-theme-base" />
                </button>
            </div>

            <!-- Weekday Headers -->
            <div
                class="grid grid-cols-7 gap-0.5 px-2 py-1 text-xs font-medium text-theme-muted text-center"
            >
                <div v-for="day in weekDays" :key="day">{{ day }}</div>
            </div>

            <!-- Calendar Days -->
            <div class="grid grid-cols-7 gap-0.5 px-2 pb-3">
                <button
                    v-for="(day, index) in calendarDays"
                    :key="index"
                    type="button"
                    :disabled="day.disabled"
                    :class="[
                        'w-7 h-7 text-xs rounded-md transition',
                        day.isToday ? 'border border-theme-primary' : '',
                        day.isSelected
                            ? 'bg-theme-primary text-white font-semibold hover:opacity-90'
                            : '',
                        !day.isSelected && !day.isOtherMonth && !day.disabled
                            ? 'hover:bg-theme-hover text-theme-base'
                            : '',
                        day.isOtherMonth ? 'text-theme-muted' : '',
                        day.disabled
                            ? 'text-theme-disabled cursor-not-allowed'
                            : 'cursor-pointer'
                    ]"
                    @click="selectDate(day)"
                >
                    {{ day.day }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";

import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import ClockIcon from "@/components/icons/ClockIcon.vue";

export default {
    name: "DateHourPicker",
    components: {
        ChevronLeftIcon,
        ChevronRightIcon,
        ClockIcon
    },
    props: {
        value: {
            type: [String, Date],
            default: null
        },
        locale: {
            type: String,
            default: "es"
        },
        min: {
            type: [String, Date],
            default: null
        },
        max: {
            type: [String, Date],
            default: null
        },
        size: {
            type: String,
            default: "md",
            validator: (value) => ["sm", "md", "lg"].includes(value)
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: "Selecciona fecha y hora"
        },
        alwaysOpen: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const now = DateTime.now();
        const initialDateTime = this.value
            ? this.value instanceof Date
                ? DateTime.fromJSDate(this.value)
                : DateTime.fromISO(this.value)
            : null;

        return {
            showCalendar: false,
            currentMonth: initialDateTime
                ? initialDateTime.month - 1
                : now.month - 1,
            currentYear: initialDateTime ? initialDateTime.year : now.year,
            selectedDate: initialDateTime,
            selectedHour: initialDateTime ? initialDateTime.hour : now.hour,
            selectedMinute: initialDateTime
                ? initialDateTime.minute
                : now.minute,
            monthNames: [
                "Enero",
                "Febrero",
                "Marzo",
                "Abril",
                "Mayo",
                "Junio",
                "Julio",
                "Agosto",
                "Septiembre",
                "Octubre",
                "Noviembre",
                "Diciembre"
            ],
            weekDays: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
        };
    },
    computed: {
        formattedDateTime() {
            if (!this.selectedDate) return "";
            const day = String(this.selectedDate.day).padStart(2, "0");
            const month = String(this.selectedDate.month).padStart(2, "0");
            const year = this.selectedDate.year;
            const hour = String(this.selectedHour).padStart(2, "0");
            const minute = String(this.selectedMinute).padStart(2, "0");
            return `${day}/${month}/${year} ${hour}:${minute}`;
        },
        hours() {
            return Array.from({ length: 24 }, (_, i) => i);
        },
        minutes() {
            return Array.from({ length: 60 }, (_, i) => i);
        },
        calendarDays() {
            const days = [];
            const firstDay = DateTime.fromObject({
                year: this.currentYear,
                month: this.currentMonth + 1,
                day: 1
            });
            const lastDay = firstDay.endOf("month");
            const prevLastDay = firstDay.minus({ months: 1 }).endOf("month");

            const firstDayWeek = firstDay.weekday % 7;
            const lastDate = lastDay.day;
            const prevLastDate = prevLastDay.day;

            const today = DateTime.now().startOf("day");

            // Previous month days
            for (let i = firstDayWeek - 1; i >= 0; i--) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth,
                    day: prevLastDate - i
                });
                days.push({
                    day: prevLastDate - i,
                    date: date,
                    isOtherMonth: true,
                    isToday: false,
                    isSelected: false,
                    disabled: this.isDateDisabled(date)
                });
            }

            // Current month days
            for (let i = 1; i <= lastDate; i++) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth + 1,
                    day: i
                });
                days.push({
                    day: i,
                    date: date,
                    isOtherMonth: false,
                    isToday: date.startOf("day").equals(today),
                    isSelected:
                        this.selectedDate &&
                        date
                            .startOf("day")
                            .equals(this.selectedDate.startOf("day")),
                    disabled: this.isDateDisabled(date)
                });
            }

            // Next month days
            const remainingDays = 42 - days.length;
            for (let i = 1; i <= remainingDays; i++) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth + 2,
                    day: i
                });
                days.push({
                    day: i,
                    date: date,
                    isOtherMonth: true,
                    isToday: false,
                    isSelected: false,
                    disabled: this.isDateDisabled(date)
                });
            }

            return days;
        },
        dropdownPosition() {
            return "left-0";
        }
    },
    watch: {
        value(newVal) {
            const dateTime = newVal
                ? newVal instanceof Date
                    ? DateTime.fromJSDate(newVal)
                    : DateTime.fromISO(newVal)
                : null;

            this.selectedDate = dateTime;
            if (dateTime) {
                this.selectedHour = dateTime.hour;
                this.selectedMinute = dateTime.minute;
                this.currentMonth = dateTime.month - 1;
                this.currentYear = dateTime.year;
            }
        }
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        toggleCalendar() {
            if (this.disabled) return;
            this.showCalendar = !this.showCalendar;
        },
        closeCalendar() {
            this.showCalendar = false;
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.closeCalendar();
            }
        },
        previousMonth() {
            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear--;
            } else {
                this.currentMonth--;
            }
        },
        nextMonth() {
            if (this.currentMonth === 11) {
                this.currentMonth = 0;
                this.currentYear++;
            } else {
                this.currentMonth++;
            }
        },
        selectDate(day) {
            if (day.disabled) return;

            this.selectedDate = day.date.set({
                hour: this.selectedHour,
                minute: this.selectedMinute
            });
            this.emitDateTime();
        },
        updateDateTime() {
            if (!this.selectedDate) return;

            this.selectedDate = this.selectedDate.set({
                hour: this.selectedHour,
                minute: this.selectedMinute
            });
            this.emitDateTime();
        },
        emitDateTime() {
            if (!this.selectedDate) return;

            const dateTimeWithTime = this.selectedDate.set({
                hour: this.selectedHour,
                minute: this.selectedMinute,
                second: 0,
                millisecond: 0
            });

            const isoString = dateTimeWithTime.toUTC().toISO();
            this.$emit("input", isoString);
        },
        isDateDisabled(date) {
            const compareDate = date.startOf("day");

            if (this.min) {
                const minDate = (
                    this.min instanceof Date
                        ? DateTime.fromJSDate(this.min)
                        : DateTime.fromISO(this.min)
                ).startOf("day");
                if (compareDate < minDate) return true;
            }

            if (this.max) {
                const maxDate = (
                    this.max instanceof Date
                        ? DateTime.fromJSDate(this.max)
                        : DateTime.fromISO(this.max)
                ).startOf("day");
                if (compareDate > maxDate) return true;
            }

            return false;
        }
    }
};
</script>
